export type GovernedDestinationStatus = 'available' | 'unavailable'

export interface GovernedDestination {
  description: string
  href?: string
  id: string
  label: string
  status: GovernedDestinationStatus
}

const destinationAllowlist = new Set([
  'https://www.theparadoxband.com/',
  'https://theparadoxmerch.myshopify.com/',
  'https://open.spotify.com/artist/6GhcI55xfZf5vqmmNqYzxW',
  'https://fanlink.tv/theparadoxband',
  'https://laylo.com/theparadoxband',
  'https://www.instagram.com/theparadoxband/',
  'https://www.tiktok.com/@theparadoxband',
  'https://x.com/TheParadoxBand1',
].map((href) => new URL(href).toString()))

export const artistMerchOrigin = 'https://theparadoxmerch.myshopify.com'

const merchCollectionPaths = new Set([
  '/collections/all',
  '/collections/hoodies',
  '/collections/nsfw',
  '/collections/t-shirts',
])

export function isAllowlistedMerchChildDestination(href: string | undefined): href is string {
  if (!href) return false
  try {
    const url = new URL(href)
    if (url.origin !== artistMerchOrigin || url.username || url.password || url.hash) return false
    if (url.pathname === '/') return true
    if (merchCollectionPaths.has(url.pathname)) return true
    if (/^\/products\/[a-z0-9-]+$/.test(url.pathname)) return true
    return /^\/cart\/[0-9]+:[1-9][0-9]?(,[0-9]+:[1-9][0-9]?)*$/.test(url.pathname)
  } catch {
    return false
  }
}

export function isAllowlistedDestination(href: string | undefined): href is string {
  if (!href) return false
  try {
    return destinationAllowlist.has(new URL(href).toString())
  } catch {
    return false
  }
}

export const artistSiteDestination: GovernedDestination = {
  id: 'artist-site',
  label: 'The Paradox website',
  description: 'Music, tour news, and more.',
  href: 'https://www.theparadoxband.com/',
  status: 'available',
}

export const artistMerchDestination: GovernedDestination = {
  id: 'artist-merch',
  label: 'Artist merch store',
  description: 'Shop The Paradox merchandise.',
  href: 'https://theparadoxmerch.myshopify.com/',
  status: 'available',
}

export const governedOfficialDestinations: readonly GovernedDestination[] = [
  artistSiteDestination,
  artistMerchDestination,
  {
    id: 'spotify',
    label: 'Spotify',
    description: 'Listen to The Paradox on Spotify.',
    href: 'https://open.spotify.com/artist/6GhcI55xfZf5vqmmNqYzxW',
    status: 'available',
  },
  {
    id: 'fanlink',
    label: 'Music links',
    description: 'Choose a current music destination.',
    href: 'https://fanlink.tv/theparadoxband',
    status: 'available',
  },
  {
    id: 'laylo',
    label: 'Message list',
    description: 'Join The Paradox on Laylo.',
    href: 'https://laylo.com/theparadoxband',
    status: 'available',
  },
  {
    id: 'instagram',
    label: 'Instagram',
    description: 'Open The Paradox on Instagram.',
    href: 'https://www.instagram.com/theparadoxband/',
    status: 'available',
  },
  {
    id: 'tiktok',
    label: 'TikTok',
    description: 'Open The Paradox on TikTok.',
    href: 'https://www.tiktok.com/@theparadoxband',
    status: 'available',
  },
  {
    id: 'x',
    label: 'X',
    description: 'Open The Paradox on X.',
    href: 'https://x.com/TheParadoxBand1',
    status: 'available',
  },
] as const

export const governedMerchDestinations: readonly GovernedDestination[] = [
  artistMerchDestination,
  {
    id: 'tour-merch-updates',
    label: 'Tour merchandise updates',
    description: 'No city merch update yet.',
    status: 'unavailable',
  },
] as const
